import { BaseKey, useList, useOne } from '@refinedev/core';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Company from '@/model/General/Company';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { Ticket } from '@/model/Ticket/Ticket';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

const useCaseResolutionShow = (caseId?: BaseKey) => {
  const { packageItemRepository, serviceOrderRepository } = useRepositoriesContext();
  const [initializedPackageItems, setInitializedPackageItems] = useState(false);
  const [packageItems, setPackageItems] = useState<PackageItem[]>([]);
  const [serviceOrderItems, setServiceOrderItems] = useState<ServiceOrderItem[]>([]);
  const {
    result,
    query: { isLoading: isLoadingTicket, isPending: isPendingTicket, refetch: refetchTicket },
  } = useOne<Ticket>({ resource: RESOURCES.TICKETS, id: caseId });
  // Envíos asociados al ticket
  const {
    result: { data: shipments },
    query: {
      isLoading: isLoadingShipments,
      isPending: isPendingShipments,
      refetch: refetchShipments,
    },
  } = useList<Shipment>({
    resource: RESOURCES.SHIPMENTS,
    pagination: { pageSize: 1000 },
    filters: [{ field: 'ticket', operator: 'eq', value: caseId }],
  });
  // Recepciones asociadas al ticket
  const {
    result: { data: receptions },
    query: {
      isLoading: isLoadingReceptions,
      isPending: isPendingReceptions,
      refetch: refetchReceptions,
    },
  } = useList<Reception>({
    resource: RESOURCES.RECEPTIONS,
    pagination: { pageSize: 1000 },
    filters: [{ field: 'ticket', operator: 'eq', value: caseId }],
  });
  const {
    result: { data: serviceOrders },
    query: { isLoading: isLoadingServiceOrders, refetch: refetchServiceOrders },
  } = useList<ServiceOrder>({
    resource: RESOURCES.SERVICE_ORDERS,
    pagination: { pageSize: 1000 },
    filters: [{ field: 'tickets', operator: 'eq', value: caseId }],
  });
  const involvedCompanies: { logisticCompanies: Company[]; paymentCompanies: Company[] } =
    useMemo(() => {
      const logisticCompaniesData: Company[] = [];
      const paymentCompaniesData: Company[] = [];

      shipments?.forEach(s => {
        const company = s.company;
        const paymentCompany = s.shipment_payment_company;

        if (company && !logisticCompaniesData.some(c => c.id === company.id)) {
          logisticCompaniesData.push(company);
        }

        if (paymentCompany && !paymentCompaniesData.some(c => c.id === paymentCompany.id)) {
          paymentCompaniesData.push(paymentCompany);
        }
      });

      receptions?.forEach(r => {
        const company = r.company;

        if (company && !logisticCompaniesData.some(c => c.id === company.id)) {
          logisticCompaniesData.push(company);
        }
      });

      return { logisticCompanies: logisticCompaniesData, paymentCompanies: paymentCompaniesData };
    }, [receptions, shipments]);

  useEffect(() => {
    if (
      !initializedPackageItems &&
      !isPendingTicket &&
      !isPendingReceptions &&
      !isPendingShipments
    ) {
      const packages = [
        ...(receptions?.map(r => r.id) ?? []),
        ...(shipments?.map(s => s.id) ?? []),
      ];

      if (packages.length > 0) {
        void packageItemRepository.getPackageItemsFromPackages(packages).then(packagesItems => {
          setInitializedPackageItems(true);

          setPackageItems(packagesItems);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receptions, result, shipments]);

  useEffect(() => {
    if (serviceOrders?.length == 1) {
      const serviceOrderIds = serviceOrders[0].id;

      void serviceOrderRepository.getServiceOrderItems(serviceOrderIds).then(serviceOrderItems => {
        setServiceOrderItems(serviceOrderItems);
      });
    }
  }, [serviceOrders, serviceOrderRepository]);
  const loadingData = useMemo(
    () =>
      isLoadingTicket ||
      isLoadingServiceOrders ||
      isLoadingShipments ||
      isLoadingReceptions ||
      !initializedPackageItems,
    [
      initializedPackageItems,
      isLoadingReceptions,
      isLoadingServiceOrders,
      isLoadingShipments,
      isLoadingTicket,
    ],
  );
  const refetch = useCallback(() => {
    void refetchTicket().then(() => {
      void Promise.all([refetchShipments(), refetchReceptions(), refetchServiceOrders()]).then(
        () => {
          void packageItemRepository
            .getPackageItemsFromPackages([
              ...(shipments?.map(s => s.id) ?? []),
              ...(receptions?.map(r => r.id) ?? []),
            ])
            .then(setPackageItems);

          if (serviceOrders?.length == 1) {
            const serviceOrderIds = serviceOrders[0].id;

            void serviceOrderRepository
              .getServiceOrderItems(serviceOrderIds)
              .then(serviceOrderItems => {
                setServiceOrderItems(serviceOrderItems);
              });
          }
        },
      );
    });
  }, [
    packageItemRepository,
    receptions,
    refetchReceptions,
    refetchServiceOrders,
    refetchShipments,
    refetchTicket,
    serviceOrderRepository,
    serviceOrders,
    shipments,
  ]);
  /**
   * @description Lista de ids de recepciones que pueden ser resueltas
   * Una recepción puede ser resuelta si:
   * - El área de trabajo del usuario es logística
   * - Todos los items asociados a la recepción están en estado de resolución 'NO_REPARADO_Y_FINALIZADO' o 'REPARADO_Y_FINALIZADO'
   */

  useEffect(() => {
    if (!caseId) {
      setPackageItems([]);
      setInitializedPackageItems(false);
    }
  }, [caseId]);

  return {
    loadingData,
    ticket: result,
    logisticCompanies: involvedCompanies.logisticCompanies,
    paymentCompanies: involvedCompanies.paymentCompanies,
    shipments: shipments ?? [],
    receptions: receptions ?? [],
    serviceOrders: serviceOrders ?? [],
    packageItems,
    serviceOrderItems,
    refetch,
  };
};

export default useCaseResolutionShow;
